<template>
  <div>
    <b-popover
      class="notifications-pop"
      :target="target"
      triggers="click blur"
      placement="bottom"
    >
      <div class="notifications-pop-content ap-popover">
        <div class="notifications-pop-head ap-popover__header">
          <div>
            <span class="ap-title ap-title__section">
              <i
                v-if="type === NOTIFICATION_TYPE.NOTIFICATIONS"
                class="fal fa-envelope mr-2"
                aria-hidden="true"
              />
              <i
                v-if="type === NOTIFICATION_TYPE.ALARMS"
                class="fal fa-bell mr-2"
                aria-hidden="true"
              />
              <span
                v-if="markAsRead && count > 0"
                class="mr-1"
              >{{ count }}</span>
              <span v-if="type === NOTIFICATION_TYPE.NOTIFICATIONS">
                <span>{{ $tc('global.notification', count) }}</span>
              </span>
              <span v-if="type === NOTIFICATION_TYPE.ALARMS">
                <span>{{ $tc('global.alarm', count) }}</span>
              </span>
            </span>
          </div>
          <b-button
            class="btn__link"
            size="sm"
            :class="{'disabled': !markAsRead || count < 1}"
            @click="updateMarkAsRead()"
          >
            {{ $t('notifications.markAllAsRead') }}
          </b-button>
        </div>
        <div
          v-if="markAsRead && count > 0"
          class="notification-list"
        >
          <b-card
            v-for="(item, index) in notifications"
            :key="index"
            class="notification-card component-bg float-component-bordered mb-2 mx-2"
            :class="{'notifications-border': type === NOTIFICATION_TYPE.NOTIFICATIONS, 'alarms-border': type === NOTIFICATION_TYPE.ALARMS}"
          >
            <div class="d-flex">
              <div
                class="entity-icon d-flex justify-content-center"
                @click="showNotificationDetailModal(item)"
              >
                <i
                  v-if="isEvent(item)"
                  class="far fa-calendar-alt mt-1 main"
                  aria-hidden="true"
                />
                <i
                  v-else
                  :class="getIconForEntity(item.entityType) + ' mt-1 main'"
                  aria-hidden="true"
                />
              </div>
              <div class="card-items d-flex flex-column px-2">
                <div class="d-flex justify-content-between align-items-center">
                  <span
                    class="entity-title"
                    @click="showNotificationDetailModal(item)"
                  >{{ item.subject }}</span>
                  <small><i
                    class="far fa-clock mr-1 my-1"
                    aria-hidden="true"
                  />{{ item.timestamp | ago }}</small>
                </div>
                <span class="my-1">
                  <span
                    v-if="viewMore"
                    class="description"
                    v-html="item.body"
                  >
                    <span
                      v-if="viewMore"
                      class="view-more"
                      @click="viewMore = !viewMore"
                    >{{ $t('notifications.viewLess') }}</span>
                  </span>
                  <span
                    v-else
                    class="description"
                    v-html="item.body.substr(0, 100)"
                  >
                    <span
                      v-if="!viewMore && item.body.length > 100"
                      class="view-more"
                      @click="viewMore = !viewMore"
                    ><span>...</span>{{ $t('notifications.viewMore') }}</span>
                  </span>
                </span>
              </div>
            </div>
          </b-card>
          <span v-if="totalElements > count">{{ $t('notifications.showingElementsOfTotalElements', { count: count, totalElements: totalElements }) }}</span>
        </div>
        <div
          v-if="!markAsRead || count === 0"
          class="all-read-section d-flex align-items-center justify-content-center"
        >
          <b-spinner
            v-if="spinner && count > 0"
            variant="primary"
            label="Spinning"
          />
          <div
            v-if="(!spinner && type === NOTIFICATION_TYPE.NOTIFICATIONS) || (count < 1 && type=== NOTIFICATION_TYPE.NOTIFICATIONS)"
            class="d-flex flex-column align-items-center justify-content-center"
          >
            <i
              class="fal fa-envelope mr-1 mb-3"
              aria-hidden="true"
            />
            <span>{{ $t('notifications.noRecentNotifications') }}</span>
          </div>
          <div
            v-if="(!spinner && type === NOTIFICATION_TYPE.ALARMS) || (count < 1 && type === NOTIFICATION_TYPE.ALARMS)"
            class="d-flex flex-column align-items-center justify-content-center"
          >
            <i
              class="fal fa-bell mr-1 mb-3"
              aria-hidden="true"
            />
            <span>{{ $t('notifications.noRecentAlarms') }}</span>
          </div>
        </div>
        <div class="notifications-pop-footer ap-popover__footer d-flex justify-content-center">
          <b-button
            id="notificationsListSeeAll"
            class="btn__link py-2"
            @click="showNotificationModal"
          >
            <p
              v-if="type === NOTIFICATION_TYPE.NOTIFICATIONS"
              class="mb-0"
            >
              {{ $t('notifications.seeAllNotifications') }}
            </p>
            <p
              v-else-if="type === NOTIFICATION_TYPE.ALARMS"
              class="mb-0"
            >
              {{ $t('notifications.seeAllAlarms') }}
            </p>
          </b-button>
        </div>
      </div>
    </b-popover>
    <modal-wrapper ref="notificationModalWrapper">
      <notification-modal
        ref="notificationModal"
        :type="type"
        @reload="loadNotifications"
      />
    </modal-wrapper>
    <modal-wrapper ref="notificationDetailModalWrapper">
      <notification-detail-modal
        ref="notificationDetailModal"
        @reload="loadNotifications"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import NotificationModal from '@/components/NavBar/notifications/NotificationModal'
import NotificationDetailModal from '@/components/NavBar/notifications/NotificationDetailModal'
import notificationEntityEnum from '@/components/NavBar/notifications/services/notificationEntityEnum'
import notificationService from '@/components/NavBar/notifications/services/notificationService'
import { NOTIFICATION_TYPE } from '@/components/NavBar/notifications/constants/notificationTypes'

export default {
    name: 'NotificationListPopover',
    components: {
        NotificationModal,
        NotificationDetailModal
    },
    props: {
        target: { type: String, required: true },
        type: { type: String, required: true },
        count: { type: Number, required: true },
        notifications: { type: Array, required: true },
        totalElements: { type: Number }
    },
    data() {
        return {
            markAsRead: true,
            spinner: true,
            viewMore: false
        }
    },
    computed: {
        NOTIFICATION_TYPE() {
            return NOTIFICATION_TYPE
        }
    },
    methods: {
        async updateMarkAsRead() {
            this.markAsRead = false
            this.spinner = false
            const [err] = await this.toCall(notificationService.updateAllNotificationAsRead(this.type === NOTIFICATION_TYPE.ALARMS))
            if (err) {
                this.showErrorModalGlobal()
            }
            this.$emit('reload')
            this.spinner = false
        },
        showNotificationModal() {
            this.$refs.notificationModalWrapper.show()
        },
        showNotificationDetailModal(item) {
            this.$refs.notificationDetailModalWrapper.show([item])
        },
        getIconForEntity(entity) {
            return notificationEntityEnum.getEntityByEnum(entity).icon
        },
        loadNotifications() {
            this.$emit('reload')
        },
        isEvent(notification) {
            return notification.notificationType === NOTIFICATION_TYPE.PERSONNEL_EVENT_MANAGED || notification.notificationType === NOTIFICATION_TYPE.NEW_UNCONFIRMED_PERSONNEL_EVENT
        }
    }
}

</script>

<style lang="scss">

.notification-nav {
  .popover-body {
    padding: 0;
  }
}

</style>

<style scoped lang="scss">

.view-more{
  cursor: pointer;
  transition: .3s;
  color: vt-color(primary);
}

.view-more:hover{
  color: vt-color(dark-primary);
}

.notification-card{
  -webkit-box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.1);
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.1);
}

.notification-card.notifications-border{
  border-left: 5px solid vt-color(orange-medium);
}

.notification-card.alarms-border{
  border-left: 5px solid rgb(209, 56, 56);
}

.notification-card .card-body{
  padding: 7px 5px;
}

.entity-icon{
  font-size: font-size(very-big);
  width: 10%;
}

.entity-icon i{
  cursor: pointer;
  transition: .3s;
}

.entity-icon i:hover{
  color: vt-color(dark-primary);
}

.card-items{
  width: 90%;
}

.card-items small{
  color: vt-color(base-text);
}

.card-items .entity-title{
  font-size: font-size(small);
  color: vt-color(primary);
  transition: .3s;
  cursor: pointer;
}

.card-items .entity-title:hover{
  color: vt-color(dark-primary);
}

.card-items .description{
  color: vt-color(dark-text);
}

.notification-list {
  overflow-y: auto;
  max-height: 250px;
}

.all-read-section{
  height: 150px;
  color: vt-color(base-text);
}

.all-read-section i{
  font-size: 40px;
}

.all-read-section span{
  font-size: font-size(big);
}

.link-btn{
  background: transparent !important;
  color: vt-color(primary);
  border: transparent !important;
}

.link-btn:hover,
.link-btn:focus,
.link-btn:active{
  color: vt-color(dark-primary) !important;
}

.badgeListClean{
  font-weight: normal;
  color: vt-color(primary);
  transition: .3s;
  cursor: pointer;
}

.badgeListClean:hover{
  color: vt-color(dark-primary);
}

.badgeListClean.disabled{
  color: vt-color(base-text);
  cursor: default;
}

.notifications-pop-content {
  width: 400px;
}

.notifications-pop-head {
  display: flex;
  justify-content: space-between;
  padding: 5px 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.notifications-pop-title {
  margin-right: .5rem;
  font-weight: bold;
  color: vt-color(dark-text);
}

.notifications-pop-footer{
  border-top: 1px solid #e0e0e0;
}

</style>
